import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import axios from 'axios';
import { getAuthToken, setAuthToken, clearAuthToken } from '../utils/authToken';
import { resyncPush } from '../utils/pushNotifications';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Non-httpOnly hint that a session *may* exist. Lets us skip the /api/auth/me
// probe for anonymous visitors (e.g. the public landing page) so the browser
// never logs a noisy 401. The httpOnly cookie remains the backend source of truth.
const SESSION_HINT = 'lumera_session';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    // No prior session hint → anonymous visitor. Skip the probe entirely so we
    // don't fire a guaranteed-401 request on public pages.
    if (localStorage.getItem(SESSION_HINT) !== '1' && !getAuthToken()) {
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
      // Only forget the session on a real auth failure — a transient network
      // or 5xx error shouldn't silently log the user out on the next reload.
      if (error?.response?.status === 401) {
        localStorage.removeItem(SESSION_HINT);
        clearAuthToken();
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Keep the session hint in sync with auth state. Covers login, signup and the
  // OAuth callback (all of which set `user`) without duplicating logic.
  useEffect(() => {
    if (user) localStorage.setItem(SESSION_HINT, '1');
  }, [user]);

  // Refresh the phone's push subscription each session so daily verse alerts keep arriving.
  const userId = user?.user_id;
  useEffect(() => {
    if (userId) resyncPush();
  }, [userId]);

  const login = async (email, password) => {
    const formData = new FormData();
    formData.append('username', email);
    formData.append('password', password);

    const response = await axios.post(`${BACKEND_URL}/api/auth/login`, formData, {
      withCredentials: true
    });
    setAuthToken(response.data.session_token);
    setUser(response.data.user);
    return response.data;
  };

  const signup = async (email, password, name) => {
    const response = await axios.post(
      `${BACKEND_URL}/api/auth/signup`,
      { email, password, name, language: localStorage.getItem('appLanguage') || 'en' },
      { withCredentials: true }
    );
    setAuthToken(response.data.session_token);
    setUser(response.data.user);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, {
        withCredentials: true
      });
    } finally {
      setUser(null);
      localStorage.removeItem(SESSION_HINT);
      clearAuthToken();
    }
  };

  const value = {
    user,
    loading,
    login,
    signup,
    logout,
    setUser,
    refreshUser: checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
